import update from 'immutability-helper';
import { create } from 'zustand';
import { SOCIAL_PLATFORMS } from '~/constants';
import { SupportedCharts } from '~/screens/CreatorAudienceDemographics';

import { TalentChartProps } from '../../blocks/TalentCharts/TalentCharts';

const initialState = {
  isModalOpen: false,
  platform: undefined,
  selectedDemographics: [],
  selectedMetrics: [],
  callback: undefined,
};

type PlatformAnalyticsStore = {
  isModalOpen: boolean;
  platform?: SOCIAL_PLATFORMS;
  selectedDemographics: TalentChartProps['demographics'];
  selectedMetrics: TalentChartProps['metrics'];

  callback?: (
    demographics?: PlatformAnalyticsStore['selectedDemographics'],
    metrics?: PlatformAnalyticsStore['selectedMetrics'],
  ) => void;

  setIsModalOpen: (
    isModalOpen: PlatformAnalyticsStore['isModalOpen'],
    callback?: PlatformAnalyticsStore['callback'],
    initialSelections?: {
      selectedDemographics?: PlatformAnalyticsStore['selectedDemographics'];
      selectedMetrics?: PlatformAnalyticsStore['selectedMetrics'];
    },
  ) => void;

  setPlatform: (platform: PlatformAnalyticsStore['platform']) => void;
  setSelectedDemographics: (key: SupportedCharts) => void;
  setSelectedMetrics: (metric: string) => void;
  setAllAnalytics: (
    demographics: TalentChartProps['demographics'],
    metrics: TalentChartProps['metrics'],
  ) => void;

  reset: () => void;
};

export const usePlatformAnalyticsStore = create<PlatformAnalyticsStore>(
  (set) => ({
    ...initialState,

    setIsModalOpen: (isModalOpen, callback, initialSelections) =>
      set((store) => {
        const newState = {
          ...store,
          isModalOpen,
          callback,
        };

        if (initialSelections?.selectedDemographics !== undefined) {
          newState.selectedDemographics =
            initialSelections.selectedDemographics;
        }

        if (initialSelections?.selectedMetrics !== undefined) {
          newState.selectedMetrics = initialSelections.selectedMetrics;
        }

        return newState;
      }),
    setPlatform: (platform) => set({ platform }),
    setSelectedDemographics: (key) =>
      set((store) => {
        const index = store.selectedDemographics.findIndex((v) => v === key);

        if (index >= 0) {
          // Exists - hence remove
          return {
            selectedDemographics: update(store.selectedDemographics, {
              $splice: [[index, 1]],
            }),
          };
        }
        // Otherwise add
        return { selectedDemographics: [...store.selectedDemographics, key] };
      }),
    setSelectedMetrics: (metric) =>
      set((store) => {
        const index = store.selectedMetrics.findIndex((v) => v === metric);

        if (index >= 0) {
          // Exists - hence remove
          return {
            selectedMetrics: update(store.selectedMetrics, {
              $splice: [[index, 1]],
            }),
          };
        }
        // Otherwise add
        return { selectedMetrics: [...store.selectedMetrics, metric] };
      }),
    setAllAnalytics: (demographics, metrics) =>
      set(() => ({
        selectedDemographics: demographics,
        selectedMetrics: metrics,
      })),
    reset: () => set({ ...initialState }),
  }),
);
